/**
 * ManualTradingDialogs — all modal dialogs of the Manual Trading page,
 * extracted from Meta5ManualTrading.jsx (P3 refactor). Pure presentation:
 * state + action handlers stay in the parent.
 */
import { Loader2, Edit3 } from 'lucide-react';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FieldPair, TpSlDualInput } from './OrderFormBits';

// ─── Add manual MT5 account ─────────────────────────────
export function AddAccountDialog({ open, onOpenChange, isAr, newAcc, setNewAcc, saving, onSave }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0f0f10] border-white/10 text-white sm:max-w-md"
        dir={isAr ? 'rtl' : 'ltr'}
        data-testid="manual-add-dialog"
      >
        <DialogHeader>
          <DialogTitle>{isAr ? 'إضافة حساب MT5 يدوي' : 'Add manual MT5 account'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <FieldPair
            label={isAr ? 'اسم مختصر' : 'Label'}
            value={newAcc.label}
            onChange={(v) => setNewAcc({ ...newAcc, label: v })}
            placeholder={isAr ? 'مثال: MEX-1' : 'e.g. MEX-1'}
            testId="new-label"
          />
          <FieldPair
            label="Login"
            value={newAcc.login}
            onChange={(v) => setNewAcc({ ...newAcc, login: v })}
            type="number"
            placeholder="12345678"
            testId="new-login"
          />
          <FieldPair
            label="Password"
            value={newAcc.password}
            onChange={(v) => setNewAcc({ ...newAcc, password: v })}
            type="password"
            testId="new-password"
          />
          <FieldPair
            label="Server"
            value={newAcc.server}
            onChange={(v) => setNewAcc({ ...newAcc, server: v })}
            placeholder="MEX-Atlantic-Demo"
            testId="new-server"
          />
          <div className="space-y-1.5">
            <Label className="text-xs text-white/60">{isAr ? 'النوع' : 'Mode'}</Label>
            <Select value={newAcc.mode} onValueChange={(v) => setNewAcc({ ...newAcc, mode: v })}>
              <SelectTrigger className="bg-black/40 border-white/10" data-testid="new-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0f0f10] border-white/10 text-white">
                <SelectItem value="demo">Demo</SelectItem>
                <SelectItem value="real">Real</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 text-xs"
            data-testid="new-cancel"
          >
            {isAr ? 'إلغاء' : 'Cancel'}
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold disabled:opacity-50 text-xs"
            data-testid="new-save"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (isAr ? 'حفظ' : 'Save')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Delete account confirm ─────────────────────────────
export function DeleteAccountDialog({ pendingDelete, onOpenChange, isAr, deleting, onConfirm }) {
  return (
    <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && onOpenChange(null)}>
      <AlertDialogContent className="bg-[#0f0f10] border-white/10 text-white" dir={isAr ? 'rtl' : 'ltr'}>
        <AlertDialogHeader>
          <AlertDialogTitle>{isAr ? 'حذف الحساب؟' : 'Delete account?'}</AlertDialogTitle>
          <AlertDialogDescription className="text-white/60">
            {isAr
              ? `سيتم حذف "${pendingDelete?.label}" نهائياً. لن تُحذف الصفقات المفتوحة عليه.`
              : `"${pendingDelete?.label}" will be permanently removed. Open positions won't be closed.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
            {isAr ? 'إلغاء' : 'Cancel'}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={deleting}
            className="bg-red-500 hover:bg-red-500/90 text-white font-bold"
            data-testid="confirm-delete"
          >
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : (isAr ? 'حذف' : 'Delete')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── Modify TP/SL on open positions ─────────────────────
export function TpslModifyDialog({ open, onOpenChange, isAr, form, setForm, modifying, onApply }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0f0f10] border-white/10 text-white sm:max-w-md"
        dir={isAr ? 'rtl' : 'ltr'}
        data-testid="tpsl-dialog"
      >
        <DialogHeader>
          <DialogTitle>{isAr ? 'تعديل TP/SL على صفقات مفتوحة' : 'Modify TP/SL on open positions'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-blue-500/[0.06] border border-blue-500/30 rounded-lg p-2.5 text-[11px] text-blue-200">
            {isAr
              ? 'سيتم تطبيق نفس SL/TP على كل الصفقات المفتوحة (اختياري: قصر التطبيق على رمز واحد).'
              : 'Same SL/TP will be applied to every open position (optionally scoped to one symbol).'}
          </div>
          <FieldPair
            label={isAr ? 'الرمز (اختياري)' : 'Symbol (optional)'}
            value={form.symbol}
            onChange={(v) => setForm({ ...form, symbol: v.toUpperCase() })}
            placeholder={isAr ? 'اتركه فارغاً للتطبيق على كل الصفقات' : 'blank = all symbols'}
            testId="tpsl-symbol"
          />
          <div className="grid grid-cols-2 gap-2">
            <TpSlDualInput
              kind="sl"
              label={isAr ? 'ستوب لوز' : 'Stop Loss'}
              accent="text-red-300"
              form={form}
              setForm={setForm}
              isAr={isAr}
            />
            <TpSlDualInput
              kind="tp"
              label={isAr ? 'تيك بروفت' : 'Take Profit'}
              accent="text-emerald-300"
              form={form}
              setForm={setForm}
              isAr={isAr}
            />
          </div>
          {(form.slMode === 'distance' || form.tpMode === 'distance') && (
            <div className="rounded-lg bg-amber-500/[0.06] border border-amber-500/30 px-2.5 py-1.5 text-[10.5px] text-amber-200 leading-snug">
              {isAr
                ? 'وضع "$" يحسب SL/TP كمسافة بالدولار من متوسط سعر فتح الصفقات المطابقة. يعمل فقط لو كانت كل الصفقات في اتجاه واحد (شراء أو بيع).'
                : '"$" mode computes SL/TP as a dollar distance from the AVG open-price of matching positions. Requires all matching positions to share the same side.'}
            </div>
          )}
        </div>
        <DialogFooter>
          <button
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 text-xs"
            data-testid="tpsl-cancel"
          >
            {isAr ? 'إلغاء' : 'Cancel'}
          </button>
          <button
            onClick={onApply}
            disabled={modifying}
            className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-500/90 text-white font-bold disabled:opacity-50 text-xs flex items-center gap-1.5"
            data-testid="tpsl-apply"
          >
            {modifying
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <><Edit3 className="h-3.5 w-3.5" />{isAr ? 'تطبيق' : 'Apply'}</>}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Cancel-all-pending confirm ─────────────────────────
export function CancelAllPendingDialog({ open, onOpenChange, isAr, onConfirm }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-[#0f0f10] border-white/10 text-white" dir={isAr ? 'rtl' : 'ltr'}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isAr ? 'إلغاء كل الأوامر المعلّقة؟' : 'Cancel every pending order?'}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-white/60">
            {isAr
              ? 'سيتم إلغاء كل الأوامر المعلّقة (limit / stop) على كل الحسابات المتصلة. الصفقات المفتوحة لن تتأثر.'
              : 'Every pending (limit/stop) order across all connected accounts will be cancelled. Open positions are unaffected.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
            {isAr ? 'إلغاء' : 'Cancel'}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-red-500 hover:bg-red-500/90 text-white font-bold"
            data-testid="confirm-cancel-all-pending"
          >
            {isAr ? 'نعم، إلغِ الكل' : 'Yes, cancel all'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── Execute template confirm ───────────────────────────
export function ExecuteTemplateDialog({ tpl, onOpenChange, isAr, connectedCount, onExecute }) {
  return (
    <AlertDialog open={!!tpl} onOpenChange={(o) => !o && onOpenChange(null)}>
      <AlertDialogContent className="bg-[#0f0f10] border-white/10 text-white" dir={isAr ? 'rtl' : 'ltr'}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isAr ? `تنفيذ قالب "${tpl?.name}"؟` : `Execute template "${tpl?.name}"?`}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-white/60">
            {tpl && (
              isAr
                ? `سيتم إرسال أمر ${tpl.mode === 'market' ? tpl.side.toUpperCase() : tpl.order_type.toUpperCase()} ${tpl.symbol} بلوت ${tpl.volume} على ${connectedCount} حساب متصل.`
                : `Dispatch ${tpl.mode === 'market' ? tpl.side.toUpperCase() : tpl.order_type.toUpperCase()} ${tpl.symbol} lot ${tpl.volume} on ${connectedCount} connected account(s).`
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
            {isAr ? 'إلغاء' : 'Cancel'}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => tpl && onExecute(tpl)}
            className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold"
            data-testid="confirm-execute-tpl"
          >
            {isAr ? 'نفّذ الآن' : 'Execute now'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── Template create / edit ─────────────────────────────
export function TemplateEditorDialog({ open, onOpenChange, isAr, form, setForm, editing, saving, onSave }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0f0f10] border-white/10 text-white sm:max-w-md max-h-[85vh] overflow-y-auto"
        dir={isAr ? 'rtl' : 'ltr'}
        data-testid="tpl-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {editing
              ? (isAr ? `تعديل: ${editing.name}` : `Edit: ${editing.name}`)
              : (isAr ? 'قالب أمر جديد' : 'New order template')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <FieldPair
            label={isAr ? 'اسم القالب' : 'Template name'}
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            placeholder={isAr ? 'مثال: Scalp XAUUSD Buy' : 'e.g. Scalp XAUUSD Buy'}
            testId="tpl-name"
          />
          <FieldPair
            label={isAr ? 'الرمز' : 'Symbol'}
            value={form.symbol}
            onChange={(v) => setForm({ ...form, symbol: v.toUpperCase() })}
            testId="tpl-symbol"
          />

          <div className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-black/40 border border-white/10">
            <button
              onClick={() => setForm({ ...form, mode: 'market' })}
              className={`py-1.5 rounded text-[11px] font-bold transition ${
                form.mode === 'market' ? 'bg-[#d4af37] text-black' : 'text-white/60'
              }`}
              data-testid="tpl-mode-market"
            >
              {isAr ? 'فوري' : 'Market'}
            </button>
            <button
              onClick={() => setForm({ ...form, mode: 'pending' })}
              className={`py-1.5 rounded text-[11px] font-bold transition ${
                form.mode === 'pending' ? 'bg-[#d4af37] text-black' : 'text-white/60'
              }`}
              data-testid="tpl-mode-pending"
            >
              {isAr ? 'معلّق' : 'Pending'}
            </button>
          </div>

          {form.mode === 'market' ? (
            <div className="grid grid-cols-2 gap-1.5">
              <button
                onClick={() => setForm({ ...form, side: 'buy' })}
                className={`py-2 rounded-lg text-xs font-bold border ${
                  form.side === 'buy' ? 'bg-emerald-500 border-emerald-500 text-black' : 'bg-white/5 border-white/10 text-white/60'
                }`}
                data-testid="tpl-side-buy"
              >
                {isAr ? 'شراء' : 'BUY'}
              </button>
              <button
                onClick={() => setForm({ ...form, side: 'sell' })}
                className={`py-2 rounded-lg text-xs font-bold border ${
                  form.side === 'sell' ? 'bg-red-500 border-red-500 text-white' : 'bg-white/5 border-white/10 text-white/60'
                }`}
                data-testid="tpl-side-sell"
              >
                {isAr ? 'بيع' : 'SELL'}
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  { id: 'buy_limit', ar: 'شراء ليمِت', en: 'Buy Limit', c: '#22c55e' },
                  { id: 'sell_limit', ar: 'بيع ليمِت', en: 'Sell Limit', c: '#ef4444' },
                  { id: 'buy_stop', ar: 'شراء ستوب', en: 'Buy Stop', c: '#10b981' },
                  { id: 'sell_stop', ar: 'بيع ستوب', en: 'Sell Stop', c: '#f97316' },
                ].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setForm({ ...form, orderType: t.id })}
                    className={`px-2 py-1.5 rounded text-[10px] font-bold border ${
                      form.orderType === t.id ? 'text-black' : 'text-white/60 bg-white/[0.03] border-white/10'
                    }`}
                    style={form.orderType === t.id ? { background: t.c, borderColor: t.c } : {}}
                    data-testid={`tpl-type-${t.id}`}
                  >
                    {isAr ? t.ar : t.en}
                  </button>
                ))}
              </div>
              <FieldPair
                label={isAr ? 'سعر الدخول' : 'Entry price'}
                value={form.entryPrice}
                onChange={(v) => setForm({ ...form, entryPrice: v })}
                type="number"
                testId="tpl-entry"
              />
            </>
          )}

          <FieldPair
            label={isAr ? 'اللوت' : 'Lot size'}
            value={form.volume}
            onChange={(v) => setForm({ ...form, volume: v })}
            type="number"
            testId="tpl-volume"
          />

          <div className="grid grid-cols-2 gap-2">
            <FieldPair
              label="Stop Loss"
              value={form.sl}
              onChange={(v) => setForm({ ...form, sl: v })}
              type="number"
              placeholder={isAr ? 'اختياري' : 'optional'}
              testId="tpl-sl"
            />
            <FieldPair
              label="Take Profit"
              value={form.tp}
              onChange={(v) => setForm({ ...form, tp: v })}
              type="number"
              placeholder={isAr ? 'اختياري' : 'optional'}
              testId="tpl-tp"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-white/60">{isAr ? 'اللون' : 'Color'}</Label>
            <div className="flex gap-1.5">
              {['#d4af37', '#22c55e', '#ef4444', '#38bdf8', '#a855f7', '#f97316'].map((c) => (
                <button
                  key={c}
                  onClick={() => setForm({ ...form, color: c })}
                  className={`w-7 h-7 rounded-full border-2 transition ${
                    form.color === c ? 'border-white scale-110' : 'border-white/20'
                  }`}
                  style={{ background: c }}
                  data-testid={`tpl-color-${c.substring(1)}`}
                />
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 text-xs"
            data-testid="tpl-cancel"
          >
            {isAr ? 'إلغاء' : 'Cancel'}
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold disabled:opacity-50 text-xs"
            data-testid="tpl-save"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (isAr ? 'حفظ' : 'Save')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
